import React from "react";
import {
  FiExternalLink,
  FiGlobe,
  FiMail,
  FiPhone,
  FiMapPin,
} from "react-icons/fi";
import { formatDate } from "utils/helperFunctions";
import { getServiceLabel } from "constants/services";

const STATUS_STYLES = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-red-100 text-red-800",
};

const formatABN = (abn) => {
  if (!abn) return null;
  return abn.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, "$1 $2 $3 $4");
};

// owner/offices/agents may arrive populated (objects) or as raw ids
const refLabel = (ref) => {
  if (!ref) return null;
  if (typeof ref === "object") return ref.name || ref.email || ref._id;
  return ref;
};

const YesNo = ({ value }) => (
  <span
    className={`px-2 py-1 rounded-full text-xs font-medium ${
      value ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
    }`}
  >
    {value ? "Yes" : "No"}
  </span>
);

export default function ContractorCompanyView({ company }) {
  if (!company) return null;

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-lightgray p-6 rounded-lg mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            {company.logo && (
              <img
                src={company.logo}
                alt={company.name}
                className="w-14 h-14 rounded-full object-cover border border-mediumgray"
              />
            )}
            <div>
              <div className="text-base font-semibold text-black mb-1">
                {company.name}
              </div>
              {company.website && (
                <a
                  href={company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <FiGlobe className="w-3 h-3" />
                  {company.website.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>
          </div>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
              STATUS_STYLES[company.status] || STATUS_STYLES.active
            }`}
          >
            {company.status || "Active"}
          </span>
        </div>
      </div>

      <div className="space-y-6">
        {/* Contact Information */}
        <div>
          <div className="font-medium text-black mb-3">Contact Information</div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            {company.email && (
              <div className="flex justify-between items-center">
                <span className="text-darkgray flex items-center gap-2">
                  <FiMail className="w-4 h-4" /> Email:
                </span>
                <a
                  href={`mailto:${company.email}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {company.email}
                </a>
              </div>
            )}
            {company.mobile && (
              <div className="flex justify-between items-center">
                <span className="text-darkgray flex items-center gap-2">
                  <FiPhone className="w-4 h-4" /> Mobile:
                </span>
                <a href={`tel:${company.mobile}`} className="text-black">
                  {company.mobile}
                </a>
              </div>
            )}
            {company.address && (
              <div className="flex justify-between items-center">
                <span className="text-darkgray flex items-center gap-2">
                  <FiMapPin className="w-4 h-4" /> Address:
                </span>
                <span className="text-black text-right max-w-[60%]">
                  {company.address}
                </span>
              </div>
            )}
            {!company.email && !company.mobile && !company.address && (
              <p className="text-darkgray italic text-center">
                No contact information available
              </p>
            )}
          </div>
        </div>

        {/* Business Information */}
        <div>
          <div className="font-medium text-black mb-3">
            Business Information
          </div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            {company.abn && (
              <div className="flex justify-between">
                <span className="text-darkgray">ABN:</span>
                <span className="text-black font-mono">
                  {formatABN(company.abn)}
                </span>
              </div>
            )}
            {company.licenseNumber && (
              <div className="flex justify-between">
                <span className="text-darkgray">License Number:</span>
                <span className="text-black">{company.licenseNumber}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-darkgray">Status:</span>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                  STATUS_STYLES[company.status] || STATUS_STYLES.active
                }`}
              >
                {company.status || "Active"}
              </span>
            </div>
            {company.website && (
              <div className="flex justify-between items-center">
                <span className="text-darkgray">Website:</span>
                <a
                  href={company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  Visit <FiExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
            {!company.abn && !company.licenseNumber && !company.website && (
              <p className="text-darkgray italic text-center">
                No business details available
              </p>
            )}
          </div>
        </div>

        {/* Services */}
        <div>
          <div className="font-medium text-black mb-3">Services</div>
          <div className="bg-lightgray rounded-lg p-4">
            {company.services?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {company.services.map((s) => (
                  <span
                    key={s}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-white border border-mediumgray text-black"
                  >
                    {getServiceLabel(s)}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-darkgray italic text-center">
                No services selected
              </p>
            )}
          </div>
        </div>

        {/* Scheduling & Booking */}
        <div>
          <div className="font-medium text-black mb-3">
            Scheduling &amp; Booking
          </div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Owner:</span>
              <span className="text-black">
                {company.owner?.name || company.owner?.email || "-"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Owner does jobs:</span>
              <YesNo value={company.ownerDoesJobs} />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Calendar invites opt-in:</span>
              <YesNo value={company.calendarOptIn} />
            </div>
            {company.calendarOptIn && company.bookingEmail && (
              <div className="flex justify-between items-center">
                <span className="text-darkgray">Booking email:</span>
                <a
                  href={`mailto:${company.bookingEmail}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {company.bookingEmail}
                </a>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-darkgray">
                Turnaround includes weekends:
              </span>
              <YesNo value={company.turnAroundWeekends} />
            </div>
          </div>
        </div>

        {/* Team */}
        <div>
          <div className="font-medium text-black mb-3">Team</div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Owner:</span>
              <span className="text-black text-right max-w-[60%]">
                {refLabel(company.owner) || "—"}
              </span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-darkgray">
                Offices ({company.offices?.length || 0}):
              </span>
              <span className="text-black text-right max-w-[60%]">
                {company.offices?.length > 0
                  ? company.offices.map(refLabel).join(", ")
                  : "—"}
              </span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-darkgray">
                Agents ({company.agents?.length || 0}):
              </span>
              <span className="text-black text-right max-w-[60%]">
                {company.agents?.length > 0
                  ? company.agents.map(refLabel).join(", ")
                  : "—"}
              </span>
            </div>
          </div>
        </div>

        {/* Payments */}
        <div>
          <div className="font-medium text-black mb-3">Payments</div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Stripe account:</span>
              <span className="text-black font-mono text-sm text-right max-w-[60%] break-all">
                {company.stripeAccountId || "Not connected"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Stripe onboarding complete:</span>
              <YesNo value={company.stripeOnboardingComplete} />
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div>
          <div className="font-medium text-black mb-3">Record Information</div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-darkgray">Created:</span>
              <span className="text-black">
                {formatDate(company.createdAt)}
              </span>
            </div>
            {company.updatedAt && (
              <div className="flex justify-between">
                <span className="text-darkgray">Last Updated:</span>
                <span className="text-black">
                  {formatDate(company.updatedAt)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
